import { motion } from "framer-motion";
import { formatDistanceToNow } from "date-fns";
import { ExternalLink, MoreVertical, RefreshCw, Newspaper } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

export interface RSSSource {
  id: string;
  name: string;
  url: string;
  logoUrl?: string;
  tier: 1 | 2;
  isEnabled: boolean;
  lastFetchedAt: string | null;
  articleCount: number;
  status: "active" | "error" | "paused";
  errorMessage?: string;
}

interface SourceCardProps {
  source: RSSSource;
  onToggle: (id: string, enabled: boolean) => void;
  onEdit: (source: RSSSource) => void;
  onDelete: (source: RSSSource) => void;
  onRefresh: (source: RSSSource) => void;
  delay?: number;
}

function getStatusIndicator(status: RSSSource["status"]) {
  switch (status) {
    case "active":
      return { color: "bg-emerald-500", label: "Active" };
    case "error":
      return { color: "bg-red-500", label: "Error" };
    case "paused":
      return { color: "bg-amber-500", label: "Paused" };
    default:
      return { color: "bg-gray-500", label: "Unknown" };
  }
}

function getTierBadge(tier: 1 | 2) {
  return tier === 1
    ? { label: "Tier 1", className: "bg-primary/10 text-primary" }
    : { label: "Tier 2", className: "bg-muted text-muted-foreground" };
}

export function SourceCard({
  source,
  onToggle,
  onEdit,
  onDelete,
  onRefresh,
  delay = 0,
}: SourceCardProps) {
  const statusIndicator = getStatusIndicator(source.status);
  const tierBadge = getTierBadge(source.tier);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.3, delay: delay * 0.05 }}
      whileHover={{ y: -2 }}
    >
      <Card
        className={cn(
          "relative overflow-hidden transition-all duration-200",
          "hover:shadow-lg hover:border-primary/20",
          !source.isEnabled && "opacity-60"
        )}
      >
        {/* Status indicator bar */}
        <div
          className={cn(
            "absolute top-0 left-0 right-0 h-1",
            statusIndicator.color
          )}
        />

        <CardContent className="pt-6 pb-4 px-4">
          {/* Header row */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              {/* Logo/Icon */}
              <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center overflow-hidden shrink-0">
                {source.logoUrl ? (
                  <img
                    src={source.logoUrl}
                    alt={source.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <Newspaper className="h-6 w-6 text-muted-foreground" />
                )}
              </div>

              {/* Name and URL */}
              <div className="min-w-0">
                <h3 className="font-semibold text-foreground truncate">
                  {source.name}
                </h3>
                <a
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1 truncate"
                >
                  <span className="truncate">{source.url}</span>
                  <ExternalLink className="h-3 w-3 shrink-0" />
                </a>
              </div>
            </div>

            {/* Actions dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onRefresh(source)}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh Now
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onEdit(source)}>
                  Edit Source
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => onDelete(source)}
                  className="text-destructive focus:text-destructive"
                >
                  Remove Source
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Badges row */}
          <div className="flex items-center gap-2 mb-4">
            <Badge variant="secondary" className={tierBadge.className}>
              {tierBadge.label}
            </Badge>
            <div className="flex items-center gap-1.5">
              <div className={cn("h-2 w-2 rounded-full", statusIndicator.color)} />
              <span className="text-xs text-muted-foreground">
                {statusIndicator.label}
              </span>
            </div>
          </div>

          {/* Error message if any */}
          {source.status === "error" && source.errorMessage && (
            <div className="mb-4 p-2 rounded-md bg-red-500/10 border border-red-500/20">
              <p className="text-xs text-red-600 dark:text-red-400">
                {source.errorMessage}
              </p>
            </div>
          )}

          {/* Stats row */}
          <div className="flex items-center justify-between text-sm mb-4">
            <div>
              <span className="text-muted-foreground">Articles: </span>
              <span className="font-medium">{source.articleCount.toLocaleString()}</span>
            </div>
            <div className="text-muted-foreground text-xs">
              {source.lastFetchedAt ? (
                <>Last fetched {formatDistanceToNow(new Date(source.lastFetchedAt))} ago</>
              ) : (
                "Never fetched"
              )}
            </div>
          </div>

          {/* Footer - Enable/Disable toggle */}
          <div className="flex items-center justify-between pt-3 border-t">
            <span className="text-sm text-muted-foreground">
              {source.isEnabled ? "Enabled" : "Disabled"}
            </span>
            <Switch
              checked={source.isEnabled}
              onCheckedChange={(checked) => onToggle(source.id, checked)}
            />
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export function SourceCardSkeleton() {
  return (
    <Card className="overflow-hidden">
      <div className="h-1 bg-muted" />
      <CardContent className="pt-6 pb-4 px-4">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <Skeleton className="h-12 w-12 rounded-lg" />
            <div>
              <Skeleton className="h-5 w-32 mb-1" />
              <Skeleton className="h-3 w-48" />
            </div>
          </div>
          <Skeleton className="h-8 w-8 rounded" />
        </div>
        <div className="flex items-center gap-2 mb-4">
          <Skeleton className="h-5 w-14 rounded-full" />
          <Skeleton className="h-4 w-16" />
        </div>
        <div className="flex items-center justify-between mb-4">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-3 w-32" />
        </div>
        <div className="flex items-center justify-between pt-3 border-t">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-5 w-10 rounded-full" />
        </div>
      </CardContent>
    </Card>
  );
}
